export const KIOSK_CLEAR_LOCK_STORAGE_KEY = 'paddiq:kiosk-clear-lock';

export type KioskClearLock = {
  stationId: string;
  grantId: string;
  expiresAt: string;
};

export type KioskClearState = {
  lock: KioskClearLock | null;
  attempt: number;
  status: 'idle' | 'clearing' | 'retrying';
};

export type KioskClearAction =
  | { type: 'begin'; lock: KioskClearLock }
  | { type: 'failed' }
  | { type: 'confirmed' };

export const idleKioskClearState: KioskClearState = {
  lock: null,
  attempt: 0,
  status: 'idle',
};

export function kioskClearReducer(
  state: KioskClearState,
  action: KioskClearAction,
): KioskClearState {
  switch (action.type) {
    case 'begin':
      return { lock: action.lock, attempt: 0, status: 'clearing' };
    case 'failed':
      return state.lock ? { ...state, attempt: state.attempt + 1, status: 'retrying' } : state;
    case 'confirmed':
      return idleKioskClearState;
  }
}

export function kioskClearRetryDelay(attempt: number) {
  return Math.min(30_000, 1_000 * 2 ** Math.max(0, attempt));
}

export function kioskIdleGrantIdentity(display: Pick<KioskClearLock, 'grantId'> | null) {
  return display?.grantId ?? null;
}

export function shouldAcceptKioskDisplayPoll(input: {
  clearState: KioskClearState;
  requestEpoch: number;
  currentEpoch: number;
}) {
  return !input.clearState.lock && input.requestEpoch === input.currentEpoch;
}

export function restoreKioskClearState(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null,
  stationId: string,
): KioskClearState {
  if (!storage) return idleKioskClearState;
  try {
    const raw = storage.getItem(KIOSK_CLEAR_LOCK_STORAGE_KEY);
    if (!raw) return idleKioskClearState;
    const parsed = JSON.parse(raw) as Partial<KioskClearLock>;
    if (
      typeof parsed.stationId !== 'string' ||
      typeof parsed.grantId !== 'string' ||
      typeof parsed.expiresAt !== 'string'
    )
      throw new Error('Invalid clear lock');
    if (parsed.stationId !== stationId) return idleKioskClearState;
    const lock = {
      stationId: parsed.stationId,
      grantId: parsed.grantId,
      expiresAt: parsed.expiresAt,
    };
    return { lock, attempt: 0, status: 'clearing' };
  } catch {
    try {
      storage.removeItem(KIOSK_CLEAR_LOCK_STORAGE_KEY);
    } catch {
      // Invalid durable state cannot be repaired while storage is unavailable.
    }
    return idleKioskClearState;
  }
}

export function persistKioskClearLock(
  storage: Pick<Storage, 'setItem'> | null,
  lock: KioskClearLock,
) {
  try {
    storage?.setItem(KIOSK_CLEAR_LOCK_STORAGE_KEY, JSON.stringify(lock));
  } catch {
    // The in-memory privacy lock remains authoritative when storage is unavailable.
  }
}

export function removeKioskClearLock(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null,
  expectedLock: KioskClearLock,
) {
  try {
    const raw = storage?.getItem(KIOSK_CLEAR_LOCK_STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as Partial<KioskClearLock>;
    if (
      stored.stationId === expectedLock.stationId &&
      stored.grantId === expectedLock.grantId &&
      stored.expiresAt === expectedLock.expiresAt
    ) {
      storage?.removeItem(KIOSK_CLEAR_LOCK_STORAGE_KEY);
    }
  } catch {
    // Best-effort cleanup must not interrupt a confirmed clear.
  }
}

export function beginKioskPrivacyClear(input: {
  lock: KioskClearLock;
  storage: Pick<Storage, 'setItem'> | null;
  fencePolls: () => void;
  enterPrivacyLock: (lock: KioskClearLock) => void;
  blankTelemetry: () => void;
}) {
  input.fencePolls();
  input.enterPrivacyLock(input.lock);
  input.blankTelemetry();
  persistKioskClearLock(input.storage, input.lock);
}

type TimerHandle = ReturnType<typeof setTimeout>;

export function startKioskIdleClearTimers(input: {
  idleMs: number;
  warningMs: number;
  onWarning: () => void;
  onClear: () => void;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle | undefined) => void;
}) {
  const setTimer = input.setTimer ?? ((callback, delayMs) => setTimeout(callback, delayMs));
  const clearTimer = input.clearTimer ?? ((handle) => clearTimeout(handle));
  let idleTimer: TimerHandle | undefined;
  let warningTimer: TimerHandle | undefined;

  const cancel = () => {
    clearTimer(idleTimer);
    clearTimer(warningTimer);
    idleTimer = undefined;
    warningTimer = undefined;
  };
  const restart = () => {
    cancel();
    idleTimer = setTimer(() => {
      input.onWarning();
      warningTimer = setTimer(input.onClear, input.warningMs);
    }, input.idleMs);
  };

  return { restart, cancel };
}

export function startKioskClearRetryLoop(input: {
  lock: KioskClearLock;
  initialAttempt?: number;
  request: (signal: AbortSignal) => Promise<void>;
  onRetry: (attempt: number) => void;
  onFinished: (result: 'confirmed') => void;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle | undefined) => void;
  requestTimeoutMs?: number;
}) {
  const setTimer = input.setTimer ?? ((callback, delayMs) => setTimeout(callback, delayMs));
  const clearTimer = input.clearTimer ?? ((handle) => clearTimeout(handle));
  const requestTimeoutMs = input.requestTimeoutMs ?? 8_000;
  let cancelled = false;
  let retryTimer: TimerHandle | undefined;
  let requestTimer: TimerHandle | undefined;
  let activeController: AbortController | null = null;

  const attemptClear = async (attempt: number) => {
    const controller = new AbortController();
    activeController = controller;
    requestTimer = setTimer(() => controller.abort(), requestTimeoutMs);
    try {
      await input.request(controller.signal);
      if (!cancelled) input.onFinished('confirmed');
    } catch {
      if (cancelled) return;
      input.onRetry(attempt + 1);
      retryTimer = setTimer(() => void attemptClear(attempt + 1), kioskClearRetryDelay(attempt));
    } finally {
      clearTimer(requestTimer);
      requestTimer = undefined;
      if (activeController === controller) activeController = null;
    }
  };

  void attemptClear(input.initialAttempt ?? 0);
  return () => {
    cancelled = true;
    activeController?.abort();
    clearTimer(requestTimer);
    clearTimer(retryTimer);
  };
}
